import { type RouteObject } from 'react-router-dom';

import { NotFoundErrorPage } from '@platform/client/components/errors/not-found-error-page';
import { PermissionDeniedErrorPage } from '@platform/client/components/errors/permission-denied-error-page';
import { AuthorizationErrorElement } from '@platform/client/routes/app';
import { IdentityErrorElement } from '@platform/client/routes/auth';
import { FeatureErrorElement } from '@platform/client/routes/features';
import { RootElement } from '@platform/client/routes/root';

import { FenrirMinimalThemeProvider } from '@packages/fenrir/fenrir-provider';

import { initFeatures } from '../utils/init-features/init-features';

import { UnifiedPortalCriticalErrorPage } from './components/unified-portal-critical-error-page';
import {
  defaultFeatureConfigs,
  defaultFeatureFlagConfigs,
} from './constants/features';
import { DashboardElement } from './dashboard';
import { authCallbackLoader } from './routes/auth-callback.loader';
import {
  organizationLoader,
  shouldRevalidateOrganization,
} from './routes/organization.loader';
import { organizationsLoader } from './routes/organizations.loader';

const {
  featureConfigs,
  featureFlagConfigs,
  routes: featureRoutes,
} = initFeatures(defaultFeatureConfigs, defaultFeatureFlagConfigs);

export const routes: RouteObject[] = [
  {
    path: '/',
    element: <RootElement />,
    errorElement: (
      <FenrirMinimalThemeProvider initialMode="light">
        <UnifiedPortalCriticalErrorPage />
      </FenrirMinimalThemeProvider>
    ),
    children: [
      {
        // needs to be outside the private routes array to remain an unprotected route that does not call loginWithRedirect
        path: '/callback',
        loader: authCallbackLoader,
        errorElement: <IdentityErrorElement />,
      },
      /** Private routes */
      {
        errorElement: <IdentityErrorElement />,
        children: [
          {
            errorElement: <AuthorizationErrorElement />,
            children: [
              {
                path: '',
                index: true,
                loader: organizationsLoader,
                lazy: () => import('./routes/_index'),
              },
              {
                path: ':organizationId',
                loader: organizationLoader,
                shouldRevalidate: shouldRevalidateOrganization,
                children: [
                  {
                    element: (
                      <DashboardElement
                        featureConfigs={featureConfigs}
                        featureFlagConfigs={featureFlagConfigs}
                      />
                    ),
                    children: [
                      {
                        errorElement: <FeatureErrorElement />,
                        children: [...featureRoutes],
                      },
                      {
                        // TODO: Remove after features have been updated to render error inline
                        path: '403',
                        element: <PermissionDeniedErrorPage />,
                      },
                      {
                        // TODO: Remove after features have been updated to render error inline
                        path: '404',
                        element: <NotFoundErrorPage />,
                      },
                      {
                        // catch-all not found
                        path: '*',
                        element: <NotFoundErrorPage />,
                      },
                    ],
                  },

                  /** Embeddable routes */
                  {},
                ],
              },
            ],
          },
        ],
      },
      /** Public routes */
      {},
    ],
  },
];
